/**
 * Supported time formats for TimeInput.tsx
 */
export const TimeFormats = {
  H_MM: "H:mm",
  HH_MM: "HH:mm",
  H_MM_SS: "H:mm:ss",
  HH_MM_SS: "HH:mm:ss",

  H_MM_a: "h:mm a",
  HH_MM_a: "hh:mm a",
  H_MM_SS_a: "h:mm:ss a",
  HH_MM_SS_a: "hh:mm:ss a",

  H_MM_A: "h:mm A",
  HH_MM_A: "hh:mm A",
  H_MM_SS_A: "h:mm:ss A",
  HH_MM_SS_A: "hh:mm:ss A",
} as const;

export type TimeFormat = (typeof TimeFormats)[keyof typeof TimeFormats];

export function timeDefaultsToString(
  time: { h: number; m: number; s: number },
  format: TimeFormat,
): string {
  return formatDateToTime(dateFromTimeParts(time), format);
}

/**
 * Only for use converting from API - string representing a timespan
 * @param time
 * @returns
 */
export function tryParseTimeToDate(time?: string): Date | null {
  if (!time) return null;

  const match = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/.exec(time);
  if (!match) return null;

  const [, h, m, s] = match.map(Number);

  return new Date(1970, 0, 1, h, m, s);
}

function dateFromTimeParts(time: { h: number; m: number; s: number }): Date {
  const baseDate = new Date(1970, 0, 1);
  baseDate.setHours(time.h, time.m, time.s, 0);
  return baseDate;
}

/**
 * Validation regex per format
 */
export const FORMAT_REGEX: Record<TimeFormat, RegExp> = {
  "H:mm": /^([0-9]|1\d|2[0-3]):([0-5]\d)$/,
  "HH:mm": /^([01]\d|2[0-3]):([0-5]\d)$/,

  "H:mm:ss": /^([0-9]|1\d|2[0-3]):([0-5]\d):([0-5]\d)$/,
  "HH:mm:ss": /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/,

  "h:mm a": /^([1-9]|1[0-2]):([0-5]\d)\s?(am|pm)$/,
  "hh:mm a": /^(0[1-9]|1[0-2]):([0-5]\d)\s?(am|pm)$/,

  "h:mm:ss a": /^([1-9]|1[0-2]):([0-5]\d):([0-5]\d)\s?(am|pm)$/,
  "hh:mm:ss a": /^(0[1-9]|1[0-2]):([0-5]\d):([0-5]\d)\s?(am|pm)$/,

  "h:mm A": /^([1-9]|1[0-2]):([0-5]\d)\s?(AM|PM)$/,
  "hh:mm A": /^(0[1-9]|1[0-2]):([0-5]\d)\s?(AM|PM)$/,

  "h:mm:ss A": /^([1-9]|1[0-2]):([0-5]\d):([0-5]\d)\s?(AM|PM)$/,
  "hh:mm:ss A": /^(0[1-9]|1[0-2]):([0-5]\d):([0-5]\d)\s?(AM|PM)$/,
};

/**
 * Convert Date -> formatted time string
 */
export function formatDateToTime(date: Date, format: TimeFormat): string {
  const h24 = date.getHours();
  const m = date.getMinutes().toString().padStart(2, "0");
  const s = date.getSeconds().toString().padStart(2, "0");

  const hasSeconds = format.includes("ss");
  const is12h = format.includes("a") || format.includes("A");

  if (is12h) {
    const isUpper = format.includes("A");
    const period = h24 >= 12 ? "PM" : "AM";
    const suffix = isUpper ? period : period.toLowerCase();

    const h12 = h24 % 12 || 12;
    const hh = format.startsWith("hh")
      ? h12.toString().padStart(2, "0")
      : h12.toString();

    return hasSeconds ? `${hh}:${m}:${s} ${suffix}` : `${hh}:${m} ${suffix}`;
  }

  const hh = format.startsWith("HH")
    ? h24.toString().padStart(2, "0")
    : h24.toString();

  return hasSeconds ? `${hh}:${m}:${s}` : `${hh}:${m}`;
}

/**
 * Convert formatted time string -> Date
 * Preserves the date portion of baseDate
 */
export function parseTimeToDate(
  time: string,
  format: TimeFormat,
  baseDate: Date,
): Date | null {
  if (!FORMAT_REGEX[format].test(time)) return null;

  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  if (format.includes("a") || format.includes("A")) {
    const match = time.match(/(\d+):(\d+)(?::(\d+))?\s?(am|pm)/i);
    if (!match) return null;

    const [, h, m, s, p] = match;
    hours = parseInt(h, 10) % 12;
    if (p.toLowerCase() === "pm") hours += 12;
    minutes = parseInt(m, 10);
    seconds = s ? parseInt(s, 10) : 0;
  } else {
    const parts = time.split(":");
    hours = parseInt(parts[0], 10);
    minutes = parseInt(parts[1], 10);
    seconds = parts[2] ? parseInt(parts[2], 10) : 0;
  }

  const next = new Date(baseDate);
  next.setHours(hours, minutes, seconds, 0);
  return next;
}

/**
 * Normalizes user-entered time strings on blur.
 *
 * Guarantees:
 * - Digit-only input is expanded (823 → 8:23)
 * - Minutes always exist
 * - AM/PM exists for 12h formats (inferred from previousValue)
 * - Output matches requested format casing
 *
 * Does NOT:
 * - Validate ranges beyond format regex
 * - Correct invalid times (e.g. 8:99)
 */

export interface NormalizeTimeOptions {
  format: TimeFormat;
  referenceValue: Date | null;
  inferMeridiem?: boolean; // default true (use previous AM/PM on edit)
}

export function normalizeTimeInput(
  text: string,
  options: NormalizeTimeOptions,
): string {
  let t = text.trim();

  // Expand digit-only input (930 → 9:30, 9 → 9:00)
  t = expandShorthandTimeInput(t, options.format);

  // Normalize AM/PM shorthand (9a → 9 am)
  t = normalizeMeridiemShorthand(t);

  // Ensure minutes exist (9 → 9:00)
  t = ensureMinutes(t, options);

  // Zero-pad single-digit minutes/seconds (9:5 → 9:05, 9:5:3 → 9:05:03)
  t = padMinutesAndSeconds(t);

  // Zero-pad a single-digit hour for padded formats (9:00 → 09:00)
  t = ensurePaddedHour(t, options.format);

  // Ensure AM/PM exists if required
  t = ensureMeridiem(t, options);

  // Ensure seconds if required by format
  t = ensureSeconds(t, options.format);

  // Normalize AM/PM casing
  t = normalizeMeridiemCasing(t, options.format);

  return t;
}

export function expandShorthandTimeInput(
  input: string,
  format: TimeFormat,
): string {
  const match = input.trim().match(/^(\d+)\s*([aApP])?$/);
  if (!match) return input;

  const digits = match[1];
  const meridiem = match[2]; // may be undefined

  const hasMinutes = format.includes("mm");
  const hasSeconds = format.includes("ss");
  const padHours = format.includes("HH") || format.includes("hh");

  let h = "0";
  let m = "00";
  let s = "00";

  // Parse shorthand digits
  if (digits.length <= 2) {
    h = digits;
  } else if (digits.length <= 4) {
    h = digits.slice(0, -2);
    m = digits.slice(-2);
  } else {
    h = digits.slice(0, -4);
    m = digits.slice(-4, -2);
    s = digits.slice(-2);
  }

  if (padHours) h = h.padStart(2, "0");

  let expanded: string;

  if (hasSeconds) {
    expanded = `${h}:${m.padStart(2, "0")}:${s.padStart(2, "0")}`;
  } else if (hasMinutes) {
    expanded = `${h}:${m.padStart(2, "0")}`;
  } else {
    expanded = h;
  }

  // Re-append meridiem shorthand if it existed
  return meridiem ? `${expanded}${meridiem}` : expanded;
}

function normalizeMeridiemShorthand(text: string): string {
  // Expand a lone trailing meridiem letter into a full am/pm token.
  // 9a -> 9 am, 9p -> 9 pm, 9:00 a -> 9:00 am, 9:00 A -> 9:00 Am (cased later)
  // A trailing "am"/"pm" ends in "m", so it is left untouched.
  return text.replace(/\s*([aApP])$/, " $1m");
}

function normalizeMeridiemCasing(text: string, format: TimeFormat): string {
  if (!formatIs12Hour(format)) return text;
  return formatUsesUppercaseMeridiem(format)
    ? text.toUpperCase()
    : text.toLowerCase();
}

function ensureMinutes(text: string, options: NormalizeTimeOptions): string {
  // If time already has minutes, do nothing
  if (/^\d{1,2}:\d{2}(\b|:)/.test(text)) {
    return text;
  }

  if (formatIs12Hour(options.format)) {
    const prevMeridiem = getPreviousMeridiem(options.referenceValue);

    return text.replace(/^(\d{1,2})(?:\s?(am|pm))?$/i, (_, h, p) => {
      const meridiem = p ?? prevMeridiem;
      return `${h}:00${meridiem ? ` ${meridiem}` : ""}`;
    });
  }

  // 24-hour format
  return text.replace(/^(\d{1,2})$/, "$1:00");
}

function padMinutesAndSeconds(text: string): string {
  // Zero-pad single-digit minute/second components while preserving the
  // hour (padded separately) and any meridiem suffix. "9:5 pm" → "9:05 pm".
  const match = text.match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?(.*)$/);
  if (!match) return text;

  const [, h, min, sec, rest] = match;
  const paddedMin = min.padStart(2, "0");
  const time =
    sec !== undefined
      ? `${h}:${paddedMin}:${sec.padStart(2, "0")}`
      : `${h}:${paddedMin}`;

  return time + rest;
}

function ensurePaddedHour(text: string, format: TimeFormat): string {
  // Padded formats (HH / hh) require a two-digit hour. The shorthand
  // expansion only pads digit-only input, so a colon-typed single-digit
  // hour (9:00 A) would otherwise fail the format regex. Pad it here.
  if (!(format.startsWith("HH") || format.startsWith("hh"))) return text;
  return text.replace(/^(\d):/, "0$1:");
}

function ensureSeconds(text: string, format: TimeFormat): string {
  if (!formatIncludesSeconds(format)) return text;

  // Already has seconds
  if (/\d{1,2}:\d{2}:\d{2}/.test(text)) return text;

  return text.replace(/(\d{1,2}:\d{2})(\s?(am|pm))?/i, "$1:00$2");
}

// Ensure AM/PM
function ensureMeridiem(text: string, options: NormalizeTimeOptions): string {
  if (!formatIs12Hour(options.format)) {
    return text;
  }

  const infer = options.inferMeridiem ?? true;

  // Already has AM/PM or inference from previous time is disabled
  if (/\b(am|pm)\b/i.test(text) || !infer) {
    return text;
  }

  const meridiem = getPreviousMeridiem(options.referenceValue);
  if (!meridiem) return text;

  return `${text} ${meridiem}`;
}

function formatIs12Hour(format: TimeFormat): boolean {
  return format.includes("a") || format.includes("A");
}

function formatUsesUppercaseMeridiem(format: TimeFormat): boolean {
  return format.includes("A");
}

function formatIncludesSeconds(format: TimeFormat): boolean {
  return format.includes("ss");
}

function getPreviousMeridiem(
  previousValue: Date | null,
): "am" | "pm" | undefined {
  if (!previousValue) return undefined;
  return previousValue.getHours() >= 12 ? "pm" : "am";
}
